import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { Button, ButtonToggle, InputBoxSingle, RoundBadge } from '@peacecover/component';
import { ArrowLeft, Check } from '@peacecover/icon';
import { useBettieSettingInfo } from '../../hooks/useBettieSettingInfo';
import { useVirtualKeyboardVisible } from '../hooks/useVirtualKeyboardVisible';
import { updateBettieMoodSelection } from '@pocapan/shared/domains/bettie/services/bettieService';
import { useGetBettieChatRoomPreferences } from '@pocapan/shared/domains/bettie/ui/hooks/useGetBettieChatRoomPreferences';
import { attachJosa, parseMemberCalledMessage } from '@pocapan/shared/utils/bettieUtils';
import { useQueryParams } from '@pocapan/shared/utils/useQueryParams';
import PulsingGradientCircle from './PulsingGradientCircle';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import { HorizontalSpeechBallon } from '../../../components/drawer/BettieNicknameUpdateBottomDrawer';
import { CircleDot } from './CircleDot';
import type { BettieSettingStep } from '../BettieSettingPage';
import type { components } from '@pocapan/shared/types/schema';
import { useQueryClient } from 'react-query';

type ApiV1MemberPersona = components['schemas']['ApiV1MemberPersona'];

const MOOD_KEYWORDS_SPEECH_STYLE = [
  t('BettieSetting.moodKeyWords.speechStyle.plain'),
  t('BettieSetting.moodKeyWords.speechStyle.cute'),
  t('BettieSetting.moodKeyWords.speechStyle.soft'),
  t('BettieSetting.moodKeyWords.speechStyle.other')
];

const MOOD_KEYWORDS_FOREIGN_SPEECH_STYLE = 'What tone works best for you?';
const LEGACY_EMPTY_SPEECH_STYLE = 'Mix casual/polite as needed';

interface SettingBettieSpeechStyleProps {
  setCurrentStep: (step: BettieSettingStep) => void;
  bettiePersona: ApiV1MemberPersona;
  chatRoomUuid: string;
  isForeignUser: boolean;
  otherMoodInputs: {
    relationship: string;
    personality: string;
    romanceStyle: string;
  };
  onBack: () => void;
  updateMode?: 'full' | 'speech_only';
}

const SettingBettieSpeechStyle = ({
  setCurrentStep,
  bettiePersona,
  chatRoomUuid,
  isForeignUser,
  otherMoodInputs,
  onBack,
  updateMode = 'full'
}: SettingBettieSpeechStyleProps) => {
  const { queryParams } = useQueryParams();
  const queryClient = useQueryClient();
  const isChangeMood = queryParams.get('isChangeMood') === 'true';

  const [speechStyleInput, setSpeechStyleInput] = useState(() =>
    isChangeMood ? '' : MOOD_KEYWORDS_SPEECH_STYLE[0]
  );
  const [isOtherInputMode, setIsOtherInputMode] = useState(false);
  const [banmalMode, setBanmalMode] = useState<'반말' | '존댓말' | undefined>(undefined);

  const { bettieSettingInfo } = useBettieSettingInfo();
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  const moodSelectionMessage = bettiePersona?.moodSelectionMessage || [];

  const { data: chatRoomPreferences } = useGetBettieChatRoomPreferences(
    isChangeMood ? chatRoomUuid : ''
  );
  const didHydrateFromPreferencesRef = useRef(false);

  // isChangeMood 진입 시: 기존에 저장된 컨텍스트 값을 기본값으로 1회만 주입
  useEffect(() => {
    if (!isChangeMood) return;
    if (!chatRoomPreferences) return;
    if (didHydrateFromPreferencesRef.current) return;

    didHydrateFromPreferencesRef.current = true;

    const predefinedKeywords = MOOD_KEYWORDS_SPEECH_STYLE.slice(0, -1);
    const rawSpeechStyle = chatRoomPreferences.speechStyle || '';
    const normalizedSpeechStyle =
      rawSpeechStyle === LEGACY_EMPTY_SPEECH_STYLE ? '' : rawSpeechStyle;

    // 레거시 값은 미선택으로 취급
    if (rawSpeechStyle === LEGACY_EMPTY_SPEECH_STYLE) {
      setSpeechStyleInput('');
      setIsOtherInputMode(false);
      setBanmalMode(undefined);
      return;
    }

    setSpeechStyleInput((prev) => prev || normalizedSpeechStyle);
    setIsOtherInputMode((prev) => {
      if (prev) return prev;
      if (!normalizedSpeechStyle) return false;
      return !predefinedKeywords.includes(normalizedSpeechStyle);
    });
    setBanmalMode((prev) => {
      if (prev) return prev;
      const next = chatRoomPreferences.banmalMode;
      if (!next || next === '정보없음') return undefined;
      return next;
    });
  }, [chatRoomPreferences, isChangeMood]);

  const handleTypingComplete = useCallback(() => {
    // 타이핑 완료 처리 (필요시 사용)
  }, []);

  const AppliedBadge = () => <RoundBadge>적용중</RoundBadge>;

  const handleNextClick = async () => {
    if (!speechStyleInput) return;

    try {
      const banmalContext = banmalMode ? { banmal_mode: banmalMode } : {};
      const context =
        updateMode === 'speech_only'
          ? {
              ...banmalContext,
              speech_style: speechStyleInput
            }
          : {
              ...banmalContext,
              relationship: otherMoodInputs.relationship,
              speech_style: speechStyleInput,
              personality: otherMoodInputs.personality,
              romance_style: otherMoodInputs.romanceStyle
            };

      const requestBody = {
        context
      };

      const response = await updateBettieMoodSelection(chatRoomUuid, requestBody);
      if (response.chatRoom.chatRoomId) {
        setCurrentStep('complete');
        queryClient.invalidateQueries(['bettieChatRoomPreferences', chatRoomUuid]);
      } else {
        throw new Error('Failed to update bettie mood selection');
      }
    } catch (error) {
      // biome-ignore lint: 에러 로깅 필요
      console.error(error);
    }
  };

  const handlePreviousClick = () => {
    onBack();
  };

  return (
    <div className="flex flex-col bg-background-light dark:bg-background-dark">
      <div className="fixed top-0 left-0 right-0 z-10 flex justify-between px-[16px] pt-[calc(env(safe-area-inset-top)+10px)] pb-[10px] bg-background-light dark:bg-background-dark">
        <ArrowLeft size={24} className="text-text-main-lv1" onClick={handlePreviousClick} />
        {!isChangeMood && (
          <button
            type="button"
            className="text-[16px] font-medium text-text-main-lv4 leading-[19px]"
            onClick={() => setCurrentStep('complete')}
          >
            {t('BettieSetting.moodSuggestion.skip')}
          </button>
        )}
      </div>
      <div className="flex flex-col items-start px-[24px] py-[10px] mt-[calc(44px+env(safe-area-inset-top))]">
        <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] whitespace-pre-line">
          {isChangeMood
            ? t('BettieSetting.moodSuggestion.changeSpeechStyleTitle', {
                nickname: bettieSettingInfo.bettieNickname
              })
            : t('BettieSetting.moodSuggestion.title', {
                nickname: attachJosa(bettieSettingInfo.bettieNickname, '가/이가')
              })}
        </h1>
        <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px] mt-[14px]">
          {isChangeMood
            ? t('BettieSetting.speechStyle.changeMoodDescription')
            : t('BettieSetting.speechStyle.description')}
        </p>
      </div>
      <div className="mt-[24px] px-[24px]">
        <div className="flex justify-center gap-[20px] items-center">
          <PulsingGradientCircle
            gradientCircleSize="56px"
            glassCircleSize="56px"
            showDottedCircle={false}
            blur="5.2px"
          >
            <ProfileAvatar
              thumbnailUrl={bettieSettingInfo.dataUrl || ''}
              widthAndHeight="53px"
              svgWidthAndHeight="53px"
            />
          </PulsingGradientCircle>
          <HorizontalSpeechBallon
            enableTyping={true}
            text={parseMemberCalledMessage(moodSelectionMessage?.speechStyle, {
              fan_name: bettieSettingInfo.userNickname,
              member_name: bettieSettingInfo.bettieNickname
            })}
            description={isForeignUser ? MOOD_KEYWORDS_FOREIGN_SPEECH_STYLE : undefined}
            onTypingComplete={handleTypingComplete}
          />
        </div>
      </div>
      <div className="px-[24px] mt-[25px] mb-[100px]">
        <div className="mt-[15px]">
          <p className="text-[12px] leading-[14px] text-text-main-lv4 mb-[7px]">
            {t('BettieSetting.speechStyle.usuallyUsed')}
          </p>
          {MOOD_KEYWORDS_SPEECH_STYLE.map((keyword) => {
            const isOther = keyword === t('BettieSetting.moodKeyWords.speechStyle.other');
            const predefinedKeywords = MOOD_KEYWORDS_SPEECH_STYLE.slice(0, -1);
            const appliedSpeechStyleRaw = chatRoomPreferences?.speechStyle || '';
            const appliedSpeechStyle =
              appliedSpeechStyleRaw === LEGACY_EMPTY_SPEECH_STYLE ? '' : appliedSpeechStyleRaw;
            const isAppliedOther =
              !!appliedSpeechStyle && !predefinedKeywords.includes(appliedSpeechStyle);
            const isApplied = isChangeMood
              ? isOther
                ? isAppliedOther
                : appliedSpeechStyle === keyword
              : false;
            const isDirectInputMode = isOther && isOtherInputMode;
            const isSelected = isOther ? isOtherInputMode : speechStyleInput === keyword;
            return (
              <div key={keyword}>
                {isDirectInputMode ? (
                  <div className="flex items-center gap-[10px] py-[18px] w-full">
                    <button
                      type="button"
                      className="flex items-center gap-[10px]"
                      onClick={() => {
                        setSpeechStyleInput('');
                        setIsOtherInputMode(false);
                      }}
                      aria-label={t('BettieSetting.moodKeyWords.speechStyle.other') as string}
                    >
                      <ButtonToggle size="lg" checked={isSelected} />
                    </button>
                    <InputBoxSingle
                      className="bg-transparent flex-1 min-w-0"
                      placeholder={t('BettieSetting.moodSuggestion.placeholder') as string}
                      value={speechStyleInput}
                      onChange={(e) => setSpeechStyleInput(e.target.value)}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      maxLength={50}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="flex items-center gap-[4px] py-[18px] w-full"
                    onClick={() => {
                      if (isOther) {
                        setSpeechStyleInput('');
                        setIsOtherInputMode(true);
                        return;
                      }
                      setSpeechStyleInput(keyword);
                      setIsOtherInputMode(false);
                    }}
                  >
                    <div className="flex items-center gap-[10px]">
                      <ButtonToggle size="lg" checked={isSelected} />
                      <p className="text-[15px] font-semibold leading-[18px] text-text-main-lv2">
                        {keyword}
                      </p>
                    </div>
                    {isApplied && <AppliedBadge />}
                  </button>
                )}

                {isSelected && (
                  <div className="pl-[34px] pb-[10px]">
                    <div className="flex items-center gap-[10px]">
                      <button
                        type="button"
                        className={`flex-1 h-[40px] inline-flex items-center justify-center rounded-[8px] border text-[14px] font-medium shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] ${
                          banmalMode === '존댓말'
                            ? 'border-primary-light dark:border-primary-dark text-primary-light dark:text-primary-dark'
                            : 'border-separator1 text-text-main-lv1'
                        }`}
                        onClick={() => setBanmalMode('존댓말')}
                      >
                        {banmalMode === '존댓말' && (
                          <Check
                            size={16}
                            className="mr-[4px] text-primary-light dark:text-primary-dark"
                          />
                        )}
                        {t('BettieSetting.speechStyle.formal')}
                      </button>
                      <button
                        type="button"
                        className={`flex-1 h-[40px] inline-flex items-center justify-center rounded-[8px] border text-[14px] font-medium shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] ${
                          banmalMode === '반말'
                            ? 'border-primary-light dark:border-primary-dark text-primary-light dark:text-primary-dark'
                            : 'border-separator1 text-text-main-lv1'
                        }`}
                        onClick={() => setBanmalMode('반말')}
                      >
                        {banmalMode === '반말' && (
                          <Check
                            size={16}
                            className="mr-[4px] text-primary-light dark:text-primary-dark"
                          />
                        )}
                        {t('BettieSetting.speechStyle.casual')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div
        className={`fixed left-0 right-0 ${isKeyboardVisible ? 'bottom-[env(safe-area-inset-bottom)]' : 'px-[24px] bottom-[calc(10px+env(safe-area-inset-bottom))]'}`}
      >
        {!isChangeMood && (
          <div className="mb-[12px] flex items-center justify-center gap-[10px]">
            {Array.from({ length: 4 }).map((_, index) => (
              <CircleDot
                key={index.toString()}
                fillColor={index === 3 ? 'var(--color-text-main-lv3)' : 'var(--color-separator2)'}
              />
            ))}
          </div>
        )}
        <Button
          size={isKeyboardVisible ? 'fill' : 'large'}
          className="w-full text-[17px] font-semibold"
          onClick={handleNextClick}
          disabled={!speechStyleInput || !banmalMode}
        >
          {t('done')}
        </Button>
      </div>
    </div>
  );
};

export default SettingBettieSpeechStyle;
